/**
 * Radiologist Workload Card
 *
 * Shows radiologists and their current workload for admin/technician
 */

import { User, Loader2, AlertCircle, Clock } from 'lucide-react'
import { useRadiologists, getTotalWorkload } from '@/application/users'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { cn } from '@/lib/utils'
import { WORKLIST_STATUS_LABELS } from '@/config/constants'

export function RadiologistWorkloadCard() {
  const { data: radiologists, isLoading, isError } = useRadiologists()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <User className="h-5 w-5" />
          Carga de Trabajo - Radiologos
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center gap-2 py-4 text-red-500">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">Error al cargar</span>
          </div>
        ) : !radiologists?.length ? (
          <p className="text-sm text-[var(--foreground-muted)] text-center py-4">
            No hay radiologos activos
          </p>
        ) : (
          <div className="space-y-3">
            {radiologists.map((radiologist) => {
              const workload = getTotalWorkload(radiologist)
              const workloadColor =
                workload === 0
                  ? 'text-green-600 dark:text-green-400'
                  : workload < 5
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-red-600 dark:text-red-400'

              return (
                <div
                  key={radiologist.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-[var(--background-subtle)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[var(--background)] rounded-full">
                      <User className="h-4 w-4 text-[var(--foreground-muted)]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{radiologist.fullName}</p>
                      <p className="text-xs text-[var(--foreground-muted)]">
                        {radiologist.pendingCount} {WORKLIST_STATUS_LABELS.PENDING.toLowerCase()} - {radiologist.inProgressCount} {WORKLIST_STATUS_LABELS.IN_PROGRESS.toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-[var(--foreground-muted)]" />
                    <span className={cn('text-lg font-semibold', workloadColor)}>
                      {workload}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
